import _ from "lodash";
import React from "react";
import { Button, Modal, Checkbox, ToggleSwitch } from "@toddle-edu/ds-web";
import classes from "./ChannelSettingsTab.scss";
import classNames from "classnames";
import {
  changeChannelToPrivate,
  handleArchiveChannel,
  handleArchiveDepartmentChannel,
  leaveChannel,
  leaveDepartmentChannel,
  optimisticToggleChatMute,
  updateChannelSettingV2,
  hideMembersListForRoles,
} from "MessagingV2/modules/MessagingModule";
import { useDispatch } from "Hooks/useDispatch";
import { useI18n } from "Hooks";
import {
  CHAT_TYPE_ENUM,
  isMessagingPublicChannelEnabled,
} from "MessagingV2/modules/Utils";
import {
  ArchiveOutlined,
  BellCancelledOutlined,
  FamilyOutlined,
  GlobeOutlined,
  LockOutlined,
  StudentOutlined,
  MultipleUsersOutlined,
} from "@toddle-edu/ds-icons";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { getIsParent, getUserId, getUserType } from "modules/CommonSelectors";
import { isIdEqual } from "Utils/idHelpers";

const OPTION_TYPES = {
  CHECKBOX: "checkbox",
  TOGGLE: "toggle",
};

const VIEW_ONLY_ROLE_TYPES = {
  STUDENT: "student",
  PARENT: "parent",
};

const styles = {
  modalFooter: {
    padding: "24px",
  },
  archiveIconStyle: {
    marginBottom: "6px",
  },
};

const SETTINGS = {
  DISABLE_STUDENT_MESSAGES: "disable_students_messages",
  DISABLE_FAMILY_MEMBER_MESSAGES: "disable_family_members_messages",
  SHOW_MEMBERS_LIST_FOR_FAMILY_MEMBER: "show_members_list_for_family_member",
  MUTE_UNMUTE_CHANNEL: "mute_unmute_channel",
};

const getSettingValue = ({
  key,
  viewOnlyRoles,
  isMuted,
  memberListHiddenRoles,
}) => {
  switch (key) {
    case SETTINGS.DISABLE_STUDENT_MESSAGES:
      return _.includes(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.STUDENT);
    case SETTINGS.DISABLE_FAMILY_MEMBER_MESSAGES:
      return _.includes(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.PARENT);
    case SETTINGS.SHOW_MEMBERS_LIST_FOR_FAMILY_MEMBER:
      return !_.includes(memberListHiddenRoles, PARENT);
    case SETTINGS.MUTE_UNMUTE_CHANNEL:
      return isMuted;
    default:
      return false;
  }
};

const getUpdatedViewOnlyRoles = ({ key, viewOnlyRoles = [] }) => {
  switch (key) {
    case SETTINGS.DISABLE_STUDENT_MESSAGES:
      return _.includes(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.STUDENT)
        ? _.without(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.STUDENT)
        : [...viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.STUDENT];
    case SETTINGS.DISABLE_FAMILY_MEMBER_MESSAGES:
      return _.includes(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.PARENT)
        ? _.without(viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.PARENT)
        : [...viewOnlyRoles, VIEW_ONLY_ROLE_TYPES.PARENT];

    default:
      return viewOnlyRoles;
  }
};

const getUpdatedMemberListHiddenRoles = ({ key, memberListHiddenRoles }) => {
  const roles = memberListHiddenRoles ?? [];
  switch (key) {
    case SETTINGS.SHOW_MEMBERS_LIST_FOR_FAMILY_MEMBER:
      return _.includes(roles, PARENT)
        ? _.without(roles, PARENT)
        : [...roles, PARENT];

    default:
      return roles;
  }
};

const SettingOption = ({ option, isChecked, onChange }) => {
  const { title, subtitle, icon: Icon, type } = option;

  if (type === OPTION_TYPES.TOGGLE) {
    return (
      <div className={classes.settingContainer}>
        <div className={classes.iconAndSettingDetailContainer}>
          <div className={classes.iconContainer}>
            <Icon variant="subtle" />
          </div>
          <div className={classes.settingDetailContainer}>
            <div className="text-body">{title}</div>
            <div className={classNames(classes.subtitleText, "text-body-s")}>
              {subtitle}
            </div>
          </div>
        </div>

        <ToggleSwitch
          dsVersion="2.0"
          checked={isChecked}
          onChange={onChange}
          size="medium"
        />
      </div>
    );
  }

  return (
    <div className={classes.settingContainer}>
      <div className={classes.iconAndSettingDetailContainer}>
        <div className={classes.iconContainer}>
          <Icon variant="subtle" />
        </div>
        <div className={classes.settingDetailContainer}>
          <div className="text-body">{title}</div>
          <div className={classNames(classes.subtitleText, "text-body-s")}>
            {subtitle}
          </div>
        </div>
      </div>

      <Checkbox onChange={onChange} isChecked={isChecked} />
    </div>
  );
};

const getOptions = ({ t, channelType, canUpdateChannel }) => {
  const checkListOptions = [
    ...(canUpdateChannel
      ? [
          {
            title: t("common:disable_messages_student_title"),
            icon: StudentOutlined,
            subtitle: t("common:disable_messages_student_subtitle"),
            key: SETTINGS.DISABLE_STUDENT_MESSAGES,
            type: OPTION_TYPES.CHECKBOX,
            requiredPermission: true,
            userTypeAccess: [STAFF],
            visibleChatTypes: [
              CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
              CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
              CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED,
            ],
          },
          {
            title: t("common:disable_messages_family_member_title"),
            subtitle: t("common:disable_messages_family_member_subtitle"),
            icon: FamilyOutlined,
            key: SETTINGS.DISABLE_FAMILY_MEMBER_MESSAGES,
            type: OPTION_TYPES.CHECKBOX,
            userTypeAccess: [STAFF],
            requiredPermission: true,
            visibleChatTypes: [
              CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
              CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
              CHAT_TYPE_ENUM.CHANNEL_FAMILY,
            ],
          },
          {
            title: t("common:show_members_list_for_family_member_title"),
            subtitle: t("common:show_members_list_for_family_member_subtitle"),
            icon: MultipleUsersOutlined,
            key: SETTINGS.SHOW_MEMBERS_LIST_FOR_FAMILY_MEMBER,
            type: OPTION_TYPES.CHECKBOX,
            userTypeAccess: [STAFF],
            requiredPermission: true,
            visibleChatTypes: [
              CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
              CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
              CHAT_TYPE_ENUM.CHANNEL_FAMILY,
              CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT,
            ],
          },
        ]
      : []),
    {
      title: t("common:mute_with_label", {
        label: t("common:channel"),
      }),
      subtitle: t("common:mute_channel_subtitle"),
      icon: BellCancelledOutlined,
      key: SETTINGS.MUTE_UNMUTE_CHANNEL,
      type: OPTION_TYPES.TOGGLE,
      userTypeAccess: [STAFF, STUDENT, PARENT],
      visibleChatTypes: [
        CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
        CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
        CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED,
        CHAT_TYPE_ENUM.CHANNEL_FAMILY,
        CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT,
      ],
    },
  ];

  return _.filter(
    checkListOptions,
    option =>
      _.includes(option.visibleChatTypes, channelType) &&
      _.includes(option.userTypeAccess, getUserType())
  );
};

const ChannelSettingsTab = props => {
  const {
    onCloseModal,
    chatId,
    viewOnlyRoles,
    channelType,
    isMuted,
    createdBy,
    canUpdateChannel,
    memberListHiddenRoles,
  } = props;

  const dispatch = useDispatch();

  const { t } = useI18n(["common"]);

  const options = getOptions({
    t,
    channelType,
    canUpdateChannel,
  });

  const creatorId = _.get(createdBy, "id");

  const isCreator = isIdEqual(creatorId, getUserId());
  const onValueChange = (key, value) => {
    switch (key) {
      case SETTINGS.DISABLE_STUDENT_MESSAGES:
      case SETTINGS.DISABLE_FAMILY_MEMBER_MESSAGES:
        {
          const updatedViewOnlyRoles = getUpdatedViewOnlyRoles({
            key,
            viewOnlyRoles,
          });
          dispatch(
            updateChannelSettingV2({
              chatId,
              viewOnlyRoles: updatedViewOnlyRoles,
            })
          );
        }
        break;

      case SETTINGS.MUTE_UNMUTE_CHANNEL:
        {
          dispatch(
            optimisticToggleChatMute({
              chatId,
              isMuted: value,
            })
          );
        }
        break;
      case SETTINGS.SHOW_MEMBERS_LIST_FOR_FAMILY_MEMBER:
        {
          const updatedMemberListHiddenRoles = getUpdatedMemberListHiddenRoles({
            key,
            memberListHiddenRoles,
          });

          dispatch(
            hideMembersListForRoles({
              chatId,
              hideMembersListFromRoles: updatedMemberListHiddenRoles,
            })
          );
        }
        break;
      default:
        break;
    }
  };

  const isPublicChannel = _.isEqual(channelType, CHAT_TYPE_ENUM.CHANNEL_PUBLIC);
  const isCustomChannel = _.isEqual(channelType, CHAT_TYPE_ENUM.CHANNEL_CUSTOM);

  const isDepartmentChannel = _.isEqual(
    channelType,
    CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT
  );

  const onArchiveClick = () => {
    if (isDepartmentChannel) {
      dispatch(handleArchiveDepartmentChannel({ isArchived: true, chatId }));
    } else {
      dispatch(handleArchiveChannel({ isArchived: true, chatId }));
    }
    onCloseModal();
  };

  const onChangeChannelToPrivate = isPrivate => {
    dispatch(changeChannelToPrivate({ chatId, isPrivate }));
    onCloseModal();
  };

  const onLeaveChannelClick = () => {
    if (isDepartmentChannel) {
      dispatch(leaveDepartmentChannel({ chatId }));
    } else {
      dispatch(leaveChannel({ chatId }));
    }
    onCloseModal();
  };

  const isPublicChannelEnable = isMessagingPublicChannelEnabled();

  const canArchiveChannel =
    (isDepartmentChannel && isCreator) ||
    (canUpdateChannel && (isPublicChannel || isCustomChannel));

  const canLeaveChannel =
    isPublicChannel || (isDepartmentChannel && getIsParent() && !isCreator);

  return (
    <>
      <Modal.Body style={styles.modalBody}>
        <div className={classes.optionsContainer}>
          {_.map(options, ({ key, ...option }) => {
            const isChecked = getSettingValue({
              key,
              viewOnlyRoles,
              isMuted,
              memberListHiddenRoles,
            });

            const onChange = () => onValueChange(key, !isChecked);
            return (
              <SettingOption
                option={option}
                key={key}
                isChecked={isChecked}
                onChange={onChange}
              />
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer style={styles.modalFooter}>
        <div className={classes.footerContainer}>
          {isPublicChannel && canUpdateChannel ? (
            <Button
              dsVersion="2.0"
              icon={<LockOutlined />}
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={() => onChangeChannelToPrivate(true)}
              isFullWidth
            >
              {t("common:change_to_private_channel")}
            </Button>
          ) : null}

          {isCustomChannel && isPublicChannelEnable && canUpdateChannel ? (
            <Button
              dsVersion="2.0"
              icon={<GlobeOutlined />}
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={() => onChangeChannelToPrivate(false)}
              isFullWidth
            >
              {t("common:change_to_public_channel")}
            </Button>
          ) : null}
          {canArchiveChannel ? (
            <Button
              dsVersion="2.0"
              icon={<ArchiveOutlined style={styles.archiveIconStyle} />}
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={onArchiveClick}
              isFullWidth
              className={classNames({
                [classes.archiveButton]: isDepartmentChannel,
              })}
            >
              {t("common:archive_label", {
                label: t("common:channel"),
              })}
            </Button>
          ) : null}
          {canLeaveChannel ? (
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={onLeaveChannelClick}
              isFullWidth
              className={classes.archiveButton}
            >
              {t("common:leave_channel")}
            </Button>
          ) : null}
        </div>
      </Modal.Footer>
    </>
  );
};

export default ChannelSettingsTab;
